import { supabase } from '../lib/supabase';

export interface Affiliate {
  id: string;
  user_id: string;
  name: string;
  email: string;
  website?: string;
  commission_rate: number;
  status: 'pending' | 'active' | 'suspended';
  payout_method?: any;
  created_at: string;
  updated_at: string;
}

export interface AffiliateLink {
  id: string;
  affiliate_id: string;
  tool_id: string;
  code: string;
  custom_url?: string;
  created_at: string;
  updated_at: string;
}

export interface AffiliateClick {
  id: string;
  link_id: string;
  ip_address?: string;
  user_agent?: string;
  referrer?: string;
  created_at: string;
}

export interface AffiliateConversion {
  id: string;
  link_id: string;
  amount: number;
  commission_amount: number;
  status: 'pending' | 'approved' | 'paid' | 'rejected';
  payout_date?: string;
  created_at: string;
  updated_at: string;
}

export const affiliateApi = {
  // Affiliate Management
  async applyForAffiliate(data: Omit<Affiliate, 'id' | 'user_id' | 'status' | 'created_at' | 'updated_at'>) {
    const { data: affiliate, error } = await supabase
      .from('affiliates')
      .insert([{
        ...data,
        user_id: (await supabase.auth.getUser()).data.user?.id,
      }])
      .select()
      .single();

    if (error) throw error;
    return affiliate;
  },

  async getAffiliateStatus() {
    const { data: affiliate, error } = await supabase
      .from('affiliates')
      .select('*')
      .eq('user_id', (await supabase.auth.getUser()).data.user?.id)
      .single();

    if (error && error.code !== 'PGRST116') throw error;
    return affiliate;
  },

  // Link Management
  async createAffiliateLink(toolId: string) {
    const { data: affiliate } = await this.getAffiliateStatus();
    if (!affiliate) throw new Error('Not an affiliate');

    const code = Math.random().toString(36).substring(2, 8).toUpperCase();
    
    const { data: link, error } = await supabase
      .from('affiliate_links')
      .insert([{
        affiliate_id: affiliate.id,
        tool_id: toolId,
        code
      }])
      .select()
      .single();

    if (error) throw error;
    return link;
  },

  async getAffiliateLinks() {
    const { data: affiliate } = await this.getAffiliateStatus();
    if (!affiliate) throw new Error('Not an affiliate');

    const { data: links, error } = await supabase
      .from('affiliate_links')
      .select(`
        *,
        ai_tools (
          name,
          affiliate_commission_rate
        )
      `)
      .eq('affiliate_id', affiliate.id);

    if (error) throw error;
    return links;
  },

  // Analytics
  async getClickStats(linkId?: string) {
    const { data: affiliate } = await this.getAffiliateStatus();
    if (!affiliate) throw new Error('Not an affiliate');

    let query = supabase
      .from('affiliate_clicks')
      .select('*')
      .eq('link_id', linkId);

    if (linkId) {
      query = query.eq('link_id', linkId);
    } else {
      query = query.in('link_id', 
        supabase
          .from('affiliate_links')
          .select('id')
          .eq('affiliate_id', affiliate.id)
      );
    }

    const { data: clicks, error } = await query;
    if (error) throw error;
    return clicks;
  },

  async getConversionStats(linkId?: string) {
    const { data: affiliate } = await this.getAffiliateStatus();
    if (!affiliate) throw new Error('Not an affiliate');

    let query = supabase
      .from('affiliate_conversions')
      .select('*');

    if (linkId) {
      query = query.eq('link_id', linkId);
    } else {
      query = query.in('link_id', 
        supabase
          .from('affiliate_links')
          .select('id')
          .eq('affiliate_id', affiliate.id)
      );
    }

    const { data: conversions, error } = await query;
    if (error) throw error;
    return conversions;
  },

  // Click Tracking
  async trackClick(code: string, referrer?: string) {
    const { data: link, error: linkError } = await supabase
      .from('affiliate_links')
      .select('id')
      .eq('code', code)
      .single();

    if (linkError) throw linkError;

    const { error: clickError } = await supabase
      .from('affiliate_clicks')
      .insert([{
        link_id: link.id,
        referrer,
        user_agent: navigator.userAgent
      }]);

    if (clickError) throw clickError;
  }
};